import * as React from 'react';
import { Form, Popup } from 'semantic-ui-react';

import Panel from 'src/modulos/Matriculas/IngresoNuevaMatricula/componentes/Panel';
import { calcularArancelAsignaturas } from 'src/modulos/Matriculas/IngresoNuevaMatricula/servicios/calculoDeValoresPorArea';
import capitalizarPrimera from 'src/modulos/Matriculas/IngresoNuevaMatricula/utils/capitalizarPrimera';
import { mapStringToOptions } from 'src/modulos/Matriculas/IngresoNuevaMatricula/utils/mapStringToOptions';
import PanelPersona, {
    Datos as PersonaDatos,
    inicial as personaInicial,
} from '../../../PanelPersona';
import SeleccionableOtro, {
    Datos as SeleccionableOtroDatos,
    inicial as seleccionableOtrosInicial,
} from '../../../SeleccionableOtro';
import ContactoEmergencia, {
    Datos as ContactoEmergenciaDatos,
    inicial as contactoEmergenciaInicial,
} from './componentes/ContactoEmergencia';
import DatosAcademicos, {
    Datos as AcademicosDatos,
    inicial as academicosInicial,
} from './componentes/DatosAcademicos';
import DatosMatricula, {
    Datos as MatriculaDatos,
    inicial as matriculaInicial,
} from './componentes/DatosMatricula';

export interface Datos extends PersonaDatos {
    viveCon: SeleccionableOtroDatos;
    pasatiempo: string;
    deporte: string;
    alergias: string;
    enfermedades: string;
    condicionMedicaEducativa: string;
    dificultadesAprendizaje: boolean;
    contactoEmergencia: ContactoEmergenciaDatos;
    academicos: AcademicosDatos;
    matricula: MatriculaDatos;
    observaciones: string;
    montoArancel: number;
    montoGastoNotarial: number;
    abonoInicial: number;
    numeroMatricula?: number;
}

export const inicial: Datos = {
    ...personaInicial,
    ocupacion: { id: 'Estudiante', otro: '' },
    viveCon: { ...seleccionableOtrosInicial },
    pasatiempo: '',
    deporte: '',
    alergias: '',
    enfermedades: '',
    condicionMedicaEducativa: '',
    dificultadesAprendizaje: false,
    contactoEmergencia: { ...contactoEmergenciaInicial },
    academicos: { ...academicosInicial },
    matricula: { ...matriculaInicial },
    observaciones: '',
    montoArancel: 0,
    montoGastoNotarial: 0,
    abonoInicial: 0,
    numeroMatricula: undefined,
};

const viveCon = [
    'Ambos padres',
    'Solo padre',
    'Solo madre',
    'Abuelos',
    'Pareja',
    'Amigo(a)',
    'Solo(a)',
    'Otro',
];

const checkboxStyle = { marginTop: '4.6ex' };

interface Props {
    mostrarBotonEliminar?: boolean;
    alumnoPropioApoderado?: boolean;
    alumno: Datos;
    onChange(alumno: Datos): void;
    onDelete?(): void;
}

class Alumno extends React.PureComponent<Props> {
    private readonly viveConOptions = mapStringToOptions(viveCon);

    public render() {
        const {
            mostrarBotonEliminar = false,
            alumnoPropioApoderado = false,
            alumno,
            onChange,
            onDelete,
        } = this.props;
        const mostrar = true;

        const titulo =
            'Datos del alumno ' + (alumnoPropioApoderado ? ' (este alumno será el apoderado)' : '');

        return (
            <PanelPersona
                titulo={titulo}
                mostrar={mostrar}
                persona={alumno}
                onChange={onChange}
                componenteInicial={
                    mostrarBotonEliminar && (
                        <Popup
                            trigger={
                                <Form.Button
                                    type='button'
                                    color='red'
                                    icon='minus'
                                    content='Quitar este alumno'
                                    onClick={onDelete}
                                />
                            }
                            content='Quita al alumno del apoderado. Esta acción no se puede deshacer.'
                        />
                    )
                }
            >
                <SeleccionableOtro
                    select={{
                        etiqueta: 'Vive con',
                        opciones: this.viveConOptions,
                    }}
                    input={{
                        etiqueta: 'Especifique',
                        mostrar: () => mostrar && alumno.viveCon.id === 'Otro',
                    }}
                    valor={alumno.viveCon}
                    onChange={value => {
                        onChange({
                            ...alumno,
                            viveCon: { ...value },
                        });
                    }}
                />

                <Form.Group widths='equal'>
                    <Form.Input
                        label='Hobby o pasatiempo'
                        placeholder='Coleccionar insectos'
                        value={alumno.pasatiempo}
                        onChange={(_e, { value }) => {
                            onChange({
                                ...alumno,
                                pasatiempo: capitalizarPrimera(value),
                            });
                        }}
                    />
                    <Form.Input
                        label='Deporte(s)'
                        placeholder='Fútbol, Bicicleta de montaña'
                        value={alumno.deporte}
                        onChange={(_e, { value }) => {
                            onChange({
                                ...alumno,
                                deporte: capitalizarPrimera(value),
                            });
                        }}
                    />
                </Form.Group>

                <Form.Group widths='equal'>
                    <Form.Input
                        label='Alergias'
                        placeholder='Paracetamol, Maní'
                        value={alumno.alergias}
                        onChange={(_e, { value }) => {
                            onChange({
                                ...alumno,
                                alergias: capitalizarPrimera(value),
                            });
                        }}
                    />
                    <Form.Input
                        label='Enfermedades'
                        placeholder='Asma'
                        value={alumno.enfermedades}
                        onChange={(_e, { value }) => {
                            onChange({
                                ...alumno,
                                enfermedades: capitalizarPrimera(value),
                            });
                        }}
                    />
                </Form.Group>

                <Form.Group widths='equal'>
                    <Form.Input
                        label='Condición médica/educativa'
                        placeholder='Crisis de ansiedad'
                        value={alumno.condicionMedicaEducativa}
                        onChange={(_e, { value }) => {
                            onChange({
                                ...alumno,
                                condicionMedicaEducativa: capitalizarPrimera(value),
                            });
                        }}
                    />
                    <Form.Checkbox
                        style={checkboxStyle}
                        label='Presenta dificultades de atención y/o aprendizaje'
                        checked={alumno.dificultadesAprendizaje}
                        onChange={(_e, { checked }) => {
                            onChange({
                                ...alumno,
                                dificultadesAprendizaje: checked || false,
                            });
                        }}
                    />
                </Form.Group>

                <ContactoEmergencia
                    contacto={alumno.contactoEmergencia}
                    onChange={this.manejarCambioContactoEmergencia}
                />

                <DatosAcademicos
                    academicos={alumno.academicos}
                    onChange={this.manejarCambioDatosAcademicos}
                    oculto={alumno.matricula.area !== 'preu'}
                />

                <DatosMatricula
                    matricula={alumno.matricula}
                    onChange={this.manejarCambioDatosMatricula}
                    alCambiarFechas={this.manejarCambioDeFechasDeMatricula}
                    numeroMatricula={alumno.numeroMatricula}
                    alCambiarNumeroMatricula={this.manejarCambioNumeroMatricula}
                />

                <Panel titulo='Observaciones de la matrícula'>
                    <Form.TextArea
                        placeholder='Escriba aquí...'
                        value={alumno.observaciones}
                        onChange={(_e, { value }) => {
                            onChange({
                                ...alumno,
                                observaciones: capitalizarPrimera((value as string) || ''),
                            });
                        }}
                    />
                </Panel>
            </PanelPersona>
        );
    }

    private readonly manejarCambioContactoEmergencia = (
        contactoEmergencia: ContactoEmergenciaDatos,
    ) => {
        const { alumno, onChange } = this.props;
        onChange({ ...alumno, contactoEmergencia });
    };

    private readonly manejarCambioDatosMatricula = (matricula: MatriculaDatos) => {
        const { alumno, onChange } = this.props;
        onChange({
            ...alumno,
            matricula,
            montoArancel: calcularArancelAsignaturas(matricula),
        });
    };

    private readonly manejarCambioDeFechasDeMatricula = (matricula: MatriculaDatos) => {
        const { alumno, onChange } = this.props;
        onChange({ ...alumno, matricula });
    };

    private readonly manejarCambioDatosAcademicos = (academicos: AcademicosDatos) => {
        const { alumno, onChange } = this.props;
        onChange({ ...alumno, academicos });
    };

    private readonly manejarCambioNumeroMatricula = (numeroMatricula?: number) => {
        const { alumno, onChange } = this.props;
        onChange({ ...alumno, numeroMatricula });
    };
}

export default Alumno;



// WEBPACK FOOTER //
// ./src/modulos/Matriculas/IngresoNuevaMatricula/componentes/S3Alumnos/componentes/Alumno/index.tsx